import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardMedia,
  Grid2,
  Typography,
  alpha,
  Chip,
  Stack,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { openNotification } from '../../../../helpers/notification';
import { Empty, Skeleton } from 'antd';
import { getConsultantV2ByUserId } from '../../../../helpers/api';
import {
  CloudUploadOutlined,
  DescriptionOutlined,
  EventNoteOutlined,
  HistoryOutlined,
} from '@mui/icons-material';
import { userCats } from '../../../../helpers/appstate';
import DocumentUploadForConsultant from '../consultants-dialogs/DocumentUploadForConsultant';
import PdfIcon from '../../../../assets/img/pdficon.png';

export default function ConsultantDocuments({ userCategory, consultantUserId }) {
  const [documentList, setDocumentList] = useState([]);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [dialogUpload, setDialogUpload] = useState(false);

  useEffect(() => {
    fetchDocumentList();
  }, []);

  useEffect(() => {
    if (refresh) fetchDocumentList();
  }, [refresh]);

  const fetchDocumentList = async () => {
    setDocumentLoading(true);
    try {
      const res = await getConsultantV2ByUserId(consultantUserId);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setDocumentList(data?.documents);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      const errmsg = error?.data?.result?.desc || 'Error loading documents';
      openNotification({ type: 'error', message: errmsg });
    } finally {
      setDocumentLoading(false);
      setRefresh(false);
    }
  };

  const handleRenderContent = () => {
    if (documentLoading) {
      return (
        <Box py={3}>
          <Skeleton active />
          <Skeleton active />
          <Skeleton active />
        </Box>
      );
    }
    if (documentList?.length < 1) {
      return (
        <Box
          height="60vh"
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor={alpha('#000', 0.02)}
          borderRadius="12px"
        >
          <Empty description="No documents uploaded yet" />
        </Box>
      );
    }
    return renderContent();
  };

  const renderContent = () => (
    <Grid2 container spacing={3}>
      {documentList?.map((doc, idx) => {
        const isExpiringSoon =
          doc?.canExpire &&
          new Date(doc?.expiryDate) < new Date(new Date().setDate(new Date().getDate() + 30));

        return (
          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }} key={idx}>
            <Card
              elevation={0}
              sx={{
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'divider',
                transition: '0.3s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: `0 8px 24px ${alpha('#0c5bab', 0.1)}`,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardActionArea
                sx={{ display: 'flex', justifyContent: 'start', p: 1.5, alignItems: 'stretch' }}
                onClick={() => window.open(doc?.documentUrl, '_blank')}
              >
                <CardMedia
                  component="img"
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: '8px',
                    objectFit: 'contain',
                    bgcolor: alpha('#F4F6F8', 0.5),
                    p: doc?.documentType === 'Pdf' ? 1 : 0,
                  }}
                  image={doc?.documentType === 'Pdf' ? PdfIcon : doc?.documentUrl}
                  alt={doc?.title}
                />

                <Box
                  sx={{
                    flex: 1,
                    px: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      noWrap
                      sx={{ maxWidth: '250px' }}
                    >
                      {doc?.title}
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                      <HistoryOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.secondary">
                        Updated{' '}
                        {new Date(doc?.updateDate).toLocaleDateString('en-NG', {
                          dateStyle: 'medium',
                        })}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    {doc?.canExpire ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <EventNoteOutlined
                          sx={{
                            fontSize: 14,
                            color: isExpiringSoon ? 'error.main' : 'text.disabled',
                          }}
                        />
                        <Typography
                          variant="caption"
                          color={isExpiringSoon ? 'error.main' : 'text.secondary'}
                          fontWeight={600}
                        >
                          Exp:{' '}
                          {new Date(doc?.expiryDate).toLocaleDateString('en-NG', {
                            dateStyle: 'medium',
                          })}
                        </Typography>
                      </Stack>
                    ) : (
                      <Chip
                        label="Lifetime"
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                      />
                    )}

                    <Chip
                      label={doc?.documentType}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        bgcolor: alpha('#0c5bab', 0.1),
                        color: 'primary.main',
                      }}
                    />
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          </Grid2>
        );
      })}
    </Grid2>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DescriptionOutlined color="action" />
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Verification Documents
          </Typography>
        </Stack>

        {userCategory === userCats.CONSULTANT && (
          <Button
            variant="contained"
            disableElevation
            startIcon={<CloudUploadOutlined />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 700,
              px: 3,
            }}
            onClick={() => setDialogUpload(true)}
          >
            Upload New
          </Button>
        )}
      </Box>

      {handleRenderContent()}

      {dialogUpload && (
        <DocumentUploadForConsultant
          open={dialogUpload}
          onClose={() => {
            setDialogUpload(false);
            setRefresh(true);
          }}
          consultantUserId={consultantUserId}
        />
      )}
    </Box>
  );
}
